import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Input, Tree } from 'antd';
import { AssetMarkerData, DeviceTimelineSegment, DeviceTimelineStatus, SnailTrailPoint } from '../../types/types';
import { DataNode } from 'antd/es/tree';
import { useSelector, useDispatch } from 'react-redux';
import { setCheckedAssets, setFocusAsset } from '../../core/device/deviceActions';
import { RootState } from '../../core/reducers';
import { getDeviceStatusColor } from '../../core/utils/mapUtils';

import './assetSearchSidebar.scss';

export const SELECT_ALL_VALUE = 'all';

interface GroupedAsset {
    division_id: number;
    division: string;
    division_color: string;
    assets: AssetMarkerData[];
}

/**
 * Construct the data for the tree. Top level is company, then divisions, then assets by division.
 * @param companyName user-facing company name to use as the top tier
 * @param assetMarkers an array of AssetMarkerData to build the tree from
 * @param checkedAssets array of strings representing asset or division leafs, e.g. division-123 or asset-1234
 */
export function buildAssetTreeData(
    companyName: string, 
    assetMarkers: AssetMarkerData[],
    checkedAssets: string[],
): DataNode[] {
    const totalAssets = assetMarkers.length;
    const assetsGroupedByDivision = new Map<number, GroupedAsset>();
    for (const assetMarker of assetMarkers || []) {
        const key = assetMarker.division_id ?? 0;
        if (!assetsGroupedByDivision.has(key)) {
            assetsGroupedByDivision.set(key, {
                division_id: key,
                division: assetMarker.division || (key === 0 ? 'Unassigned' : `Division ${key}`),
                division_color: assetMarker.division_color || '#999999',
                assets: [],
            });
        }
        assetsGroupedByDivision.get(key)!.assets.push(assetMarker);
    }
    const divisionNodes: DataNode[] = Array.from(assetsGroupedByDivision.values())
        .sort((a, b) => a.division.localeCompare(b.division))
        .map((group) => {
            const children: DataNode[] = group.assets
                .sort((a, b) => {
                    const an = a.registration || a.title || String(a.asset_id);
                    const bn = b.registration || b.title || String(b.asset_id);
                    return an.localeCompare(bn);
                })
                .map((asset) => ({
                    key: `asset-${asset.asset_id}`,
                    title: (
                        <span>
                            {`${asset.title}${asset.driver_name ? ` -  ${asset.driver_name}` : ''}`}
                        </span>
                    ),
                    isLeaf: true,
                }));
            const divisionTitle = (
                <span
                    style={{ 
                        display: 'inline-flex',
                        alignItems: 'center', 
                        gap: 8,
                    }}
                >
                    <span
                        aria-hidden
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: group.division_color,
                            display: 'inline-block',
                        }}
                    />
                    <span>
                        {group.division} <span style={{ opacity: 0.6 }}>({children.length})</span>
                    </span>
                </span>
            );
            return {
                key: `division-${group.division_id}`,
                title: divisionTitle,
                children,
            } as DataNode;
        });
    const allAssetsChecked = checkedAssets.includes(SELECT_ALL_VALUE);
    const checkedAssetsNoDivisions = checkedAssets.filter((ca) => !ca.includes('division'));
    const checkedAssetsCount = allAssetsChecked
        ? totalAssets
        : (checkedAssetsNoDivisions?.length || 0);
    const root: DataNode = {
        key: SELECT_ALL_VALUE,
        title: (<span>{companyName} ({checkedAssetsCount}/{totalAssets})</span>),
        children: divisionNodes,
    };
    return [root];
}

/**
 * Determines if a tree's leaf key is an asset or not
 * @param key the key to check
 */
function isAssetKey(key: React.Key) {
    return typeof key === 'string' && key.startsWith('asset-');
}

/**
 * Resolve DataNode to ReactNode as can be of variable type
 * @param node the node of variable type
 */
function resolveNodeTitle(node: DataNode): ReactNode {
    const t = node.title as any;
    return typeof t === 'function' ? t(node) : t;
}

/**
 * Ignore special characters to not confuse the regex match when searching
 * @param text the text to escape
 */
function escapeSpecialCharacterforRegex(text: string) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Extract string from react node
 * @param node the node to extract text from
 */
function titleToPlainString(node: ReactNode): string {
    if (node === null || node === undefined || typeof node === 'boolean') {
        return '';
    }
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(titleToPlainString).join('');
    }
    if (React.isValidElement(node)) {
        return titleToPlainString(node.props.children);
    }
    return '';
}

/**
 * Compute the status color for an asset in the sidebar so that it matches the map.
 * @param assetId the asset id to compute status for
 * @param assetMarkers an array of AssetMarkerData
 * @param sliderValue current slider value
 * @param selectedSnailTrail current selected snail trail
 * @param timelineData current timeline data for selected snail trail
 */
const computeStatusColorForAsset = (
    assetId: number,
    assetMarkers: AssetMarkerData[],
    sliderValue: number,
    selectedSnailTrail: SnailTrailPoint[] | null | undefined,
    timelineData?: DeviceTimelineSegment[] | null
): string => {
    const RED = 'var(--red-dark)' as const;
    const YELLOW = 'var(--yellow-dark)' as const;
    const GREEN = 'var(--green-dark)' as const;
    if (selectedSnailTrail?.length && timelineData?.length) {
        const sliderSecondsSinceMidnight = sliderValue * 10;
        let match: DeviceTimelineSegment | null = null;
        for (let i = 0; i < timelineData.length; i++) {
            const seg = timelineData[i];
            if (sliderSecondsSinceMidnight > seg.start && sliderSecondsSinceMidnight <= seg.end) {
                match = seg;
                break;
            }
        }
        if (match) {
            if (match.status === DeviceTimelineStatus.Moving) {
                return GREEN;
            }
            if (match.status === DeviceTimelineStatus.Idle) {
                return YELLOW;
            }
            return RED;
        }
    }
    const asset = assetMarkers.find((a) => a.asset_id === assetId);
    if (!asset || !asset.markers || !asset.markers.length) {
        return RED;
    }
    const deviceStatus = Number(asset.device_status || 0);
    let closestMarkerPoint = asset.markers[asset.markers.length - 1];
    let closestMarkerFound = false;
    asset.markers.forEach((marker) => {
        if (closestMarkerFound) {
            return;
        }
        if (marker.time_interval > sliderValue) {
            closestMarkerPoint = marker;
            closestMarkerFound = true;
        }
    });
    const isIdle = Boolean(closestMarkerPoint.is_idle);
    return getDeviceStatusColor(deviceStatus, isIdle);
};

interface AssetSearchSidebarProps {
    searchValue: string;
    onChangeSearchValue: React.ChangeEventHandler<HTMLInputElement>;
    assetMarkers: AssetMarkerData[];
    onHoverAssetLeaf?: (id: number | null) => void;
    timelineData?: DeviceTimelineSegment[] | null;
}

/**
 * Render the new filter sidebar for the rebuilt map. Houses a searchbar and asset tree.
 * The tree is sorted first by company, then divisions, then assets by division.
 */
const AssetSearchSidebar: React.FC<AssetSearchSidebarProps> = ({
    searchValue,
    onChangeSearchValue,
    assetMarkers,
    onHoverAssetLeaf = () => {},
    timelineData = null,
}) => {
    const dispatch = useDispatch();
    const companyName = useSelector((state: RootState) => state.user.userCompany.title) as string;
    const selectedSnailTrail = useSelector((state: RootState) => (state.device.selectedSnailTrail as unknown as SnailTrailPoint[]) || null);
    const sliderValue = useSelector((state: RootState) => state.device.mapSliderValue || 0);
    const checkedAssets = useSelector((state: RootState) => state.device.checkedAssets || [SELECT_ALL_VALUE]);
    const currentCheckedArray: string[] = useMemo(() => {
        if (checkedAssets.length === 1 && checkedAssets[0] === SELECT_ALL_VALUE) {
            return [SELECT_ALL_VALUE];
        }
        return Array.isArray(checkedAssets) 
            ? (checkedAssets as string[]) 
            : [];
    }, [checkedAssets]);
    const assetIdToDivisionMap = useMemo(() => {
        const map = new Map<number, React.Key>();
        for (const am of assetMarkers || []) {
            const divisionId = am.division_id ?? 0;
            map.set(am.asset_id, `division-${divisionId}`);
        }
        return map;
    }, [assetMarkers]);
    const baseTreeData = useMemo(() => {
        return buildAssetTreeData(companyName, assetMarkers, checkedAssets);
    }, [companyName, assetMarkers, checkedAssets]);
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>(() => {
        try {
            const saved = localStorage.getItem('map_tree_expanded_keys');
            return saved 
                ? JSON.parse(saved) 
                : [SELECT_ALL_VALUE];
        } catch {
            return [SELECT_ALL_VALUE];
        }
    });
    const { displayTreeData, autoExpandDivisionKeys } = useMemo(() => {
        const q = searchValue.trim().toLowerCase();
        if (!q) {
            return {
                displayTreeData: baseTreeData,
                autoExpandDivisionKeys: [] as React.Key[],
            };
        }
        const root = baseTreeData[0];
        const nextDivisions: DataNode[] = [];
        const expandKeys: React.Key[] = [];
        for (const div of root.children ?? []) {
            const divChildren = (div.children ?? []) as DataNode[];
            const matchingAssets = divChildren.filter((asset) => {
                const titleNode = resolveNodeTitle(asset);
                const plain = titleToPlainString(titleNode).toLowerCase();
                return plain.includes(q);
            });
            const divisionTitleNode = resolveNodeTitle(div);
            const divisionPlain = titleToPlainString(divisionTitleNode).toLowerCase();
            const divisionMatches = divisionPlain.includes(q);
            if (matchingAssets.length > 0 || divisionMatches) {
                nextDivisions.push({
                    ...div,
                    children: matchingAssets,
                    disabled: false,
                    isLeaf: false,
                });
                expandKeys.push(div.key as React.Key);
            } else {
                nextDivisions.push({
                    ...div,
                    title: (
                        <span
                            style={{
                                opacity: 0.6,
                                color: 'var(--cool-gray-40)',
                            }}
                        >
                            <span style={{ filter: 'grayscale(100%)' }}>
                                {resolveNodeTitle(div)}
                            </span>
                        </span>
                    ),
                    children: undefined,
                    isLeaf: true,
                    disabled: true,
                });
            }
        }
        const nextRoot: DataNode = {
            ...root,
            children: nextDivisions,
        };
        return {
            displayTreeData: [nextRoot],
            autoExpandDivisionKeys: expandKeys,
        };
    }, [baseTreeData, searchValue]);
    const validKeys = useMemo(() => {
        const keys = new Set<React.Key>();
        const walk = (nodes: DataNode[] | undefined) => {
            if (!nodes) {
                return;
            }
            for (const n of nodes) {
                keys.add(n.key);
                if (n.children && Array.isArray(n.children)) {
                    walk(n.children as DataNode[]);
                }
            }
        };
        walk(displayTreeData);
        return keys;
    }, [displayTreeData]);

    useEffect(() => {
        try {
            localStorage.setItem(
                'map_tree_expanded_keys',
                JSON.stringify(expandedKeys),
            );
        } catch {}
    }, [expandedKeys]);
    useEffect(() => {
        setExpandedKeys((prev) => prev.filter((k) => validKeys.has(k)));
    }, [validKeys]);
    useEffect(() => {
        const q = searchValue.trim();
        setExpandedKeys((prev) => {
            if (!q) {
                return prev.length 
                    ? prev 
                    : [SELECT_ALL_VALUE];
            }
            const union = new Set<React.Key>([
                ...prev,
                SELECT_ALL_VALUE,
                ...autoExpandDivisionKeys,
            ]);
            return Array.from(union);
        });
    }, [searchValue, autoExpandDivisionKeys]);
    useEffect(() => {
        setExpandedKeys((prev) => {
            const divisionsToOpen = new Set<React.Key>();
            for (const key of currentCheckedArray) {
                if (!isAssetKey(key)) {
                    continue;
                }
                const idStr = String(key).split('-')[1];
                const assetId = parseInt(idStr || '', 10);
                if (!Number.isFinite(assetId)) {
                    continue;
                }
                const divKey = assetIdToDivisionMap.get(assetId);
                if (divKey) {
                    divisionsToOpen.add(divKey);
                }
            }
            if (divisionsToOpen.size === 0) {
                return prev.length ? prev : [SELECT_ALL_VALUE];
            }
            const union = new Set<React.Key>([
                ...prev,
                SELECT_ALL_VALUE,
                ...divisionsToOpen,
            ]);
            return Array.from(union);
        });
    }, [currentCheckedArray, assetIdToDivisionMap]);

    return (
        <div
            className="map-filter-search"
            style={{
                padding: '0 8px',
                overflowX: 'auto',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
            }}
        >
            <Input.Search
                value={searchValue}
                onChange={onChangeSearchValue}
                placeholder="Search Name/Registration/Driver"
                style={{ marginBottom: '8px' }}
            />
            <Tree
                className="hide-file-icon"
                showIcon
                showLine
                treeData={displayTreeData}
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys as React.Key[])}
                checkable
                onCheck={(checkedKeys) => {
                    const val = Array.isArray(checkedKeys)
                        ? checkedKeys
                        : checkedKeys.checked;
                    dispatch(setCheckedAssets(val));
                }}
                selectable
                onSelect={(selectedKeys, info) => {
                    const key = info.node.key;
                    if (!isAssetKey(key)) {
                        return;
                    }
                    const assetId = parseInt(
                        String(key).split('-')[1] || '',
                        10,
                    );
                    if (Number.isNaN(assetId)) {
                        return;
                    }
                    const hasSelectAll =
                        currentCheckedArray.length > 0 &&
                        currentCheckedArray[0] === SELECT_ALL_VALUE;
                    const assetKey = `asset-${assetId}`;
                    if (!hasSelectAll && !currentCheckedArray.includes(assetKey)) {
                        dispatch(
                            setCheckedAssets([
                                ...currentCheckedArray,
                                assetKey,
                            ]),
                        );
                    }
                    setTimeout(() => dispatch(setFocusAsset(assetId)), 200);
                }}
                checkedKeys={checkedAssets}
                titleRender={(node) => {
                    const resolved = resolveNodeTitle(node);
                    const q = searchValue.trim();
                    if (typeof node.key === 'string' && node.key.startsWith('division-')) {
                        if (!q) {
                            return resolved;
                        }
                        const plain = titleToPlainString(resolved).toLowerCase();
                        const qLower = q.toLowerCase();
                        if (!plain.includes(qLower)) {
                            return resolved;
                        }
                        return (
                            <span
                                style={{
                                    color: 'var(--safety-orange)',
                                    fontWeight: 600,
                                }}
                            >
                                {resolved}
                            </span>
                        );
                    }
                    if (!isAssetKey(node.key)) {
                        return resolved;
                    }
                    const assetId = parseInt(
                        String(node.key).split('-')[1] || '',
                        10,
                    );

                    const renderText = () => {
                        if (!q) {
                            return resolved;
                        }
                        const raw = titleToPlainString(resolved);
                        if (!raw) {
                            return resolved;
                        }
                        const lower = raw.toLowerCase();
                        const qLower = q.toLowerCase();
                        if (!lower.includes(qLower)) {
                            return resolved;
                        }
                        const parts = raw.split(
                            new RegExp(
                                `(${escapeSpecialCharacterforRegex(q)})`,
                                'gi',
                            ),
                        );
                        return (
                            <>
                                {parts.map((part, i) =>
                                    part.toLowerCase() === qLower ? (
                                        <span
                                            key={i}
                                            style={{
                                                color: 'var(--safety-orange)',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {part}
                                        </span>
                                    ) : (
                                        <span key={i}>{part}</span>
                                    ),
                                )}
                            </>
                        );
                    };

                    // 🔴 NEW: single source of truth for status colour
                    const statusColor = Number.isFinite(assetId)
                        ? computeStatusColorForAsset(
                            assetId,
                            assetMarkers,
                            sliderValue,
                            selectedSnailTrail,
                            timelineData
                        )
                        : 'var(--red-dark)';

                    return (
                        <span
                            onMouseEnter={() =>
                                Number.isFinite(assetId) &&
                                onHoverAssetLeaf?.(assetId)
                            }
                        >
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: statusColor,
                                    lineHeight: '16px',
                                    verticalAlign: 'middle',
                                }}
                            />
                            &ensp;
                            <span
                                style={{
                                    lineHeight: '16px',
                                    display: 'inline-block',
                                    verticalAlign: 'middle',
                                }}
                            >
                                {renderText()}
                            </span>
                        </span>
                    );
                }}
            />
        </div>
    );
};

export default AssetSearchSidebar;
